import React, { useState, useEffect } from 'react';
import { db, getSettings, calculateProductPricing } from '../lib/database';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Search } from 'lucide-react';
import ProductForm from './ProductForm';
import ProductDetails from './ProductDetails';

export default function ProductsPage({ onAddProduct, closeModalsCount }) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showProductForm, setShowProductForm] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
    loadSettings();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, selectedCategory]);

  useEffect(() => {
    // Close modals when navigation requests it
    if (closeModalsCount > 0) {
      setShowProductForm(false);
      setShowProductDetails(false);
      setSelectedProduct(null);
      setEditingProduct(null);
    }
  }, [closeModalsCount]);

  const loadProducts = async () => {
    try {
      const allProducts = await db.products.orderBy('createdAt').reverse().toArray();
      setProducts(allProducts);
      
      const uniqueCategories = [...new Set(allProducts.map(p => p.category).filter(Boolean))];
      setCategories(uniqueCategories);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading products:', error);
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const settingsData = await getSettings();
      setSettings(settingsData);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(product => {
        // Search in basic fields
        const basicMatch = product.name?.toLowerCase().includes(search) ||
          product.brand?.toLowerCase().includes(search) ||
          product.model?.toLowerCase().includes(search) ||
          product.notes?.toLowerCase().includes(search);
        
        // Search in specifications
        const specsMatch = product.specifications?.some(spec =>
          spec.name?.toLowerCase().includes(search) ||
          spec.value?.toLowerCase().includes(search)
        );
        
        return basicMatch || specsMatch;
      });
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    setFilteredProducts(filtered);
  };

  const handleProductSaved = () => {
    loadProducts();
    setShowProductForm(false);
    setEditingProduct(null);
  };

  const handleProductDeleted = () => {
    loadProducts();
    setShowProductDetails(false);
    setSelectedProduct(null);
  };

  const openProductDetails = (product) => {
    setSelectedProduct(product);
    setShowProductDetails(true);
  };

  const openEditForm = (product) => {
    setEditingProduct(product);
    setShowProductForm(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p>Ürünler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full mx-auto sm:mx-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Ürün ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Category Filter */}
      {categories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory('all')}
          >
            Tümü
          </Button>
          {categories.map(category => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </Button>
          ))}
        </div>
      )}

      {/* Products List */}
      <div className="space-y-3">
        {filteredProducts.map(product => {
          const pricing = calculateProductPricing(product, settings);
          
          const formatCurrency = (amount, currency, isTL = false) => {
            const num = parseFloat(amount);
            let formatted;
            
            if (isTL) {
              // For TL conversions, remove decimals
              formatted = Math.round(num).toLocaleString();
            } else {
              // For original currency, remove .00 but keep other decimals
              formatted = num % 1 === 0 ? num.toLocaleString() : num.toLocaleString();
            }
            
            const getCurrencySymbol = (curr) => {
              switch(curr) {
                case 'USD': return '$';
                case 'EUR': return '€';
                case 'TL': return '₺';
                default: return curr;
              }
            };
            return `${getCurrencySymbol(currency)}${formatted}`;
          };
          
          return (
            <Card key={product.id} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4" onClick={() => openProductDetails(product)}>
                <div className="flex gap-4">
                  {/* Image */}
                  <div className="flex-shrink-0">
                    {product.image ? (
                      <div className="w-16 h-16 bg-muted rounded-md overflow-hidden">
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
                        <span className="text-muted-foreground text-xs">No Image</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Product Details */}
                  <div className="flex-1">
                    <div className="space-y-1">
                      <h3 className="font-semibold text-base">{product.name}</h3>
                      <div className="text-sm text-muted-foreground">
                        {product.brand && <span>{product.brand}</span>}
                        {product.model && <span>{product.brand ? ' - ' : ''}{product.model}</span>}
                        {product.category && (
                          <span className="block text-xs mt-1 text-primary">{product.category}</span>
                        )}
                      </div>
                      
                      {/* Sale Price Information */}
                      <div className=" mt-2 flex flex-row justify-between">
                      <div className="font-medium text-muted-foreground text-xs self-center">
                          {formatCurrency(pricing.netSalePrice, pricing.currency)} + KDV
                        </div>
                      <div className="text-sm text-primary">
                          {pricing.currency !== 'TL' 
                            ? formatCurrency(pricing.grossSalePriceInTl, 'TL', true)
                            : formatCurrency(pricing.grossSalePrice, 'TL', true)
                          }
                        </div>


                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredProducts.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {searchTerm || selectedCategory !== 'all' 
              ? 'Arama kriterlerinize uygun ürün bulunamadı.' 
              : 'Henüz ürün eklenmemiş. İlk ürününüzü ekleyin!'
            }
          </p>
        </div>
      )}

      {/* Product Form Modal */}
      {showProductForm && (
        <ProductForm
          product={editingProduct}
          onSave={handleProductSaved}
          onCancel={() => {
            setShowProductForm(false);
            setEditingProduct(null);
          }}
        />
      )}

      {/* Product Details Modal */}
      {showProductDetails && selectedProduct && (
        <ProductDetails
          product={selectedProduct}
          onEdit={() => {
            setShowProductDetails(false);
            openEditForm(selectedProduct);
          }}
          onDelete={handleProductDeleted}
          onClose={() => {
            setShowProductDetails(false);
            setSelectedProduct(null);
          }}
        />
      )}
    </div>
  );
}